// Eingehende Webhooks (Discord-kompatibel): Liste, anlegen (URL einmalig), bearbeiten, Avatar, Token neu, löschen.
import { FIXED_LIMITS, type ChannelDto, type WebhookDto } from '@lagerfeuer/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { webhooksApi } from '../api';
import { formatDateTime } from '../format';
import { toast } from '../toast';
import { Button, ConfirmButton, Dialog, Empty, Field, Loading, SecretReveal } from '../ui';
import { sameOriginUrl } from '../safe-url';
import {
  channelLabel,
  curlExample,
  IMAGE_ACCEPT,
  incomingKey,
  isAllowedImage,
  webhookExecUrl,
  webhooksErrorText,
  webhookTargetChannels,
} from './webhooks-utils';

function WebhookUrlDialog(props: { webhook: WebhookDto; title: string; onClose: () => void }) {
  const url = webhookExecUrl(props.webhook);
  return (
    <Dialog
      title={props.title}
      testId="webhooks-in-url-dialog"
      onClose={props.onClose}
      footer={
        <Button variant="primary" testId="webhooks-in-url-done" onClick={props.onClose}>
          Fertig
        </Button>
      }
    >
      {url ? (
        <>
          <SecretReveal label={`Webhook-URL für „${props.webhook.name}"`} value={url} testId="webhooks-in-url" note="Wird nur jetzt angezeigt – wer die URL kennt, kann posten." />
          <div className="lfx-muted">Beispiel (Discord-kompatibles Format, auch embeds/username/avatar_url):</div>
          <pre className="lfx-wh-code" data-testid="webhooks-in-curl">
            {curlExample(url)}
          </pre>
        </>
      ) : (
        <p className="lfx-muted">Der Server hat keine URL geliefert.</p>
      )}
    </Dialog>
  );
}

function CreateIncomingDialog(props: { serverId: string; channels: ChannelDto[]; onClose: () => void; onCreated: (w: WebhookDto) => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [pickedChannelId, setChannelId] = useState(props.channels[0]?.id ?? '');
  // Kanäle können nach dem Öffnen nachladen (oder der gewählte verschwinden): dann gilt der erste Kanal –
  // Anzeige (Select-Wert) und Absenden nutzen denselben Wert.
  const channelId = props.channels.some((c) => c.id === pickedChannelId) ? pickedChannelId : (props.channels[0]?.id ?? '');
  const [formError, setFormError] = useState<string | null>(null);
  const create = useMutation({
    gcTime: 0, // Einmal-Geheimnis nicht im Mutation-Cache liegen lassen
    mutationFn: (v: { channelId: string; name: string }) => webhooksApi.create(v.channelId, { name: v.name }),
    onSuccess: (w) => {
      void qc.invalidateQueries({ queryKey: incomingKey(props.serverId) });
      props.onCreated(w);
    },
  });
  return (
    <Dialog title="Eingehenden Webhook anlegen" testId="webhooks-in-create-dialog" onClose={props.onClose}>
      <form
        className="lfx-stack"
        noValidate
        data-testid="webhooks-in-create-form"
        onSubmit={(e) => {
          e.preventDefault();
          setFormError(null);
          if (!name.trim()) return setFormError('Bitte einen Namen angeben.');
          if (!channelId) return setFormError('Bitte einen Kanal wählen.');
          create.mutate({ channelId, name: name.trim() });
        }}
      >
        <Field label="Name" hint={`Höchstens ${FIXED_LIMITS.webhookNameMax} Zeichen; erscheint als Absender.`}>
          {(id) => (
            <input
              id={id}
              className="lfx-input"
              data-testid="webhooks-in-create-name"
              value={name}
              maxLength={FIXED_LIMITS.webhookNameMax}
              onChange={(e) => setName(e.target.value)}
            />
          )}
        </Field>
        <Field label="Kanal">
          {(id) => (
            <select id={id} className="lfx-select" data-testid="webhooks-in-create-channel" value={channelId} onChange={(e) => setChannelId(e.target.value)}>
              {props.channels.length === 0 && <option value="">Keine Textkanäle</option>}
              {props.channels.map((c) => (
                <option key={c.id} value={c.id}>
                  {channelLabel(c)}
                </option>
              ))}
            </select>
          )}
        </Field>
        {formError && (
          <div className="lfx-error" role="alert" data-testid="webhooks-in-create-form-error">
            {formError}
          </div>
        )}
        {create.isError && (
          <div className="lfx-error" role="alert" data-testid="webhooks-in-create-error">
            {webhooksErrorText(create.error)}
          </div>
        )}
        <div className="lfx-row lfx-wh-actions">
          <Button variant="ghost" testId="webhooks-in-create-cancel" onClick={props.onClose}>
            Abbrechen
          </Button>
          <Button type="submit" variant="primary" testId="webhooks-in-create-submit" disabled={create.isPending}>
            Anlegen
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function IncomingRow(props: { webhook: WebhookDto; channels: ChannelDto[]; serverId: string; onSecret: (w: WebhookDto, title: string) => void }) {
  const qc = useQueryClient();
  const w = props.webhook;
  const [name, setName] = useState(w.name);
  const [channelId, setChannelId] = useState(w.channelId);
  const [error, setError] = useState<string | null>(null);
  const refresh = () => void qc.invalidateQueries({ queryKey: incomingKey(props.serverId) });
  const onError = (err: unknown) => setError(webhooksErrorText(err));
  const ok = () => {
    setError(null);
    refresh();
  };

  const save = useMutation({
    mutationFn: (body: { name?: string; channelId?: string }) => webhooksApi.update(w.id, body),
    onSuccess: () => {
      toast({ kind: 'success', title: 'Webhook gespeichert' });
      ok();
    },
    onError,
  });
  const avatar = useMutation({ mutationFn: (f: File) => webhooksApi.setAvatar(w.id, f), onSuccess: ok, onError });
  const removeAvatar = useMutation({ mutationFn: () => webhooksApi.deleteAvatar(w.id), onSuccess: ok, onError });
  const regen = useMutation({
    gcTime: 0, // Einmal-Geheimnis nicht im Mutation-Cache liegen lassen
    mutationFn: () => webhooksApi.regenerateToken(w.id),
    onSuccess: (res) => {
      ok();
      props.onSecret(res, 'Neue Webhook-URL');
    },
    onError,
  });
  const remove = useMutation({ mutationFn: () => webhooksApi.remove(w.id), onSuccess: ok, onError });

  const dirty = name.trim() !== w.name || channelId !== w.channelId;
  const channel = props.channels.find((c) => c.id === w.channelId);
  return (
    <li className="lfx-card lfx-stack lfx-wh-item" data-testid="webhooks-in-row" data-webhook-id={w.id}>
      <div className="lfx-row">
        {sameOriginUrl(w.avatarUrl) ? (
          <img className="lfx-wh-avatar" src={sameOriginUrl(w.avatarUrl)!} alt="" data-testid="webhooks-in-avatar" />
        ) : (
          <span className="lfx-wh-avatar lfx-wh-avatar--empty" aria-hidden="true">
            {w.name.slice(0, 1).toUpperCase()}
          </span>
        )}
        <div className="lfx-grow">
          <div className="lfx-wh-name" data-testid="webhooks-in-name">
            {w.name}
          </div>
          <div className="lfx-muted">
            <span data-testid="webhooks-in-channel">{channelLabel(channel)}</span> · erstellt {formatDateTime(w.createdAt)}
          </div>
        </div>
        <ConfirmButton label="Löschen" confirmLabel="Wirklich löschen?" testId={`webhooks-in-delete-${w.id}`} disabled={remove.isPending} onConfirm={() => remove.mutate()} />
      </div>
      <form
        className="lfx-wh-edit"
        onSubmit={(e) => {
          e.preventDefault();
          if (!name.trim()) return setError('Der Name darf nicht leer sein.');
          const body: { name?: string; channelId?: string } = {};
          if (name.trim() !== w.name) body.name = name.trim();
          if (channelId !== w.channelId) body.channelId = channelId;
          save.mutate(body);
        }}
      >
        <input
          className="lfx-input"
          aria-label="Name"
          data-testid={`webhooks-in-name-input-${w.id}`}
          value={name}
          maxLength={FIXED_LIMITS.webhookNameMax}
          onChange={(e) => setName(e.target.value)}
        />
        <select className="lfx-select" aria-label="Kanal" data-testid={`webhooks-in-channel-select-${w.id}`} value={channelId} onChange={(e) => setChannelId(e.target.value)}>
          {!channel && <option value={w.channelId}>{channelLabel(undefined)}</option>}
          {props.channels.map((c) => (
            <option key={c.id} value={c.id}>
              {channelLabel(c)}
            </option>
          ))}
        </select>
        <Button type="submit" testId={`webhooks-in-save-${w.id}`} disabled={!dirty || save.isPending}>
          Speichern
        </Button>
      </form>
      <div className="lfx-row">
        <label className="lfx-btn lfx-btn--ghost lfx-wh-file">
          Avatar hochladen
          <input
            type="file"
            accept={IMAGE_ACCEPT}
            className="lfx-wh-file__input"
            data-testid={`webhooks-in-avatar-input-${w.id}`}
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = '';
              if (!f) return;
              if (!isAllowedImage(f)) return setError('Nur Bilder im Format PNG, JPEG, GIF oder WebP.');
              avatar.mutate(f);
            }}
          />
        </label>
        {w.avatarUrl && (
          <Button variant="ghost" testId={`webhooks-in-avatar-remove-${w.id}`} onClick={() => removeAvatar.mutate()}>
            Avatar entfernen
          </Button>
        )}
        <ConfirmButton
          label="Token neu erzeugen"
          confirmLabel="Alte URL ungültig machen?"
          testId={`webhooks-in-regen-${w.id}`}
          disabled={regen.isPending}
          onConfirm={() => regen.mutate()}
        />
      </div>
      {error && (
        <div className="lfx-error" role="alert" data-testid={`webhooks-in-error-${w.id}`}>
          {error}
        </div>
      )}
    </li>
  );
}

export function IncomingWebhooks(props: { serverId: string; channels: ChannelDto[] | undefined }) {
  const [creating, setCreating] = useState(false);
  const [secret, setSecret] = useState<{ webhook: WebhookDto; title: string } | null>(null);
  const q = useQuery({ queryKey: incomingKey(props.serverId), queryFn: () => webhooksApi.forServer(props.serverId) });
  const targets = webhookTargetChannels(props.channels);

  return (
    <section className="lfx-stack" data-testid="webhooks-in" aria-label="Eingehende Webhooks">
      <div className="lfx-row lfx-row--between">
        <p className="lfx-muted lfx-wh-note">Eingehende Webhooks posten Nachrichten von außen in einen Kanal (Discord-kompatibles JSON).</p>
        <Button variant="primary" testId="webhooks-in-create" disabled={q.isError} onClick={() => setCreating(true)}>
          Webhook anlegen
        </Button>
      </div>
      {q.isPending ? (
        <Loading testId="webhooks-in-loading" />
      ) : q.isError ? (
        <div className="lfx-error" role="alert" data-testid="webhooks-in-load-error">
          {webhooksErrorText(q.error)}
        </div>
      ) : q.data.length === 0 ? (
        <Empty testId="webhooks-in-empty">Noch keine eingehenden Webhooks.</Empty>
      ) : (
        <ul className="lfx-wh-list" data-testid="webhooks-in-list">
          {q.data.map((w) => (
            <IncomingRow key={w.id} webhook={w} channels={targets} serverId={props.serverId} onSecret={(webhook, title) => setSecret({ webhook, title })} />
          ))}
        </ul>
      )}
      {creating && (
        <CreateIncomingDialog
          serverId={props.serverId}
          channels={targets}
          onClose={() => setCreating(false)}
          onCreated={(w) => {
            setCreating(false);
            setSecret({ webhook: w, title: 'Webhook angelegt' });
          }}
        />
      )}
      {secret && <WebhookUrlDialog webhook={secret.webhook} title={secret.title} onClose={() => setSecret(null)} />}
    </section>
  );
}
